import { setContext } from '@apollo/client/link/context';
import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  concat,
  TypePolicies,
  makeVar,
} from '@apollo/client';

// Authentication link creator
export const createAuthLink = (getToken: () => Promise<string | null>) => {
  return setContext(async (_, { headers }) => {
    const token = await getToken();
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  });
};

// Create reactive variables dynamically
export const reactiveVars: Record<string, any> = {};

// Cache configuration - now only generates type policies, doesn't create variables
function generateCacheConfig(context: Record<string, any>): TypePolicies {
  const typePolicies: TypePolicies = Object.keys(context).reduce((acc, key) => {
    acc[key] = {
      fields: {
        [key]: {
          read() {
            // Use the reactive variable that was already created
            return reactiveVars[key]?.();
          },
        },
      },
    };
    return acc;
  }, {} as TypePolicies);

  return typePolicies;
}

// Auth Apollo Client creator
export const createApolloClient = (
  getToken: () => Promise<string | null>,
  context: Record<string, any>,
  apiUrl: string,
  cache?: InMemoryCache
) => {
  const authLink = createAuthLink(getToken);
  const httpLink = new HttpLink({ uri: apiUrl });

  // Create reactive variables only once, here
  Object.keys(context).forEach(key => {
    if (!reactiveVars[key]) {
      reactiveVars[key] = makeVar(context[key]);
    } else {
      // Update existing variable's value
      reactiveVars[key](context[key]);
    }
  });

  const apolloCache =
    cache ||
    new InMemoryCache({
      typePolicies: generateCacheConfig(context),
    });

  const client = new ApolloClient({
    link: concat(authLink, httpLink),
    cache: apolloCache,
  });

  return client;
};
